// Identité Ed25519 du poste — port navigateur de thor/core_client/identity.py.
//
// Schéma reproduit EXACTEMENT ce que vérifie le core (app/auth.py) :
//   message signé = `${timestamp}.${sha256hex(body)}`   (timestamp = epoch s)
//   enrôlement    = auto-signature des 32 octets de la clé publique
//
// La clé privée vit dans IndexedDB (jamais transmise). Distincte de toute licence.

import { ed25519 } from "@noble/curves/ed25519";
import { sha256 } from "@noble/hashes/sha256";
import { bytesToHex, hexToBytes, utf8ToBytes } from "@noble/hashes/utils";

import { getIdentity, putIdentity, type StoredIdentity } from "../storage/db";

/** Longueur de l'empreinte affichée et envoyée au core. */
const LONGUEUR_EMPREINTE = 16;

/**
 * Empreinte du poste : identifiant stable dérivé de la clé publique.
 *
 * UNE SEULE définition, utilisée à la fois par l'enrôlement (core/sync.ts) et
 * par l'affichage dans l'onglet Licence. Ne pas la recalculer ailleurs : une
 * empreinte affichée qui diffère de celle envoyée au serveur produirait des
 * licences liées à la mauvaise machine.
 */
export function empreinteDepuisClePublique(publicKeyHex: string): string {
  return publicKeyHex.slice(0, LONGUEUR_EMPREINTE);
}

/** Empreinte de CE poste, en chargeant l'identité si besoin. */
export async function empreinteDuPoste(): Promise<string> {
  const identite = await Identity.loadOrCreate();
  return empreinteDepuisClePublique(identite.publicKeyHex);
}

export class Identity {
  private constructor(
    private readonly priv: Uint8Array,
    readonly publicKeyHex: string,
    public clientId: string | null,
  ) {}

  /** Charge l'identité du poste, ou en génère une au premier appel. */
  static async loadOrCreate(): Promise<Identity> {
    const stored = await getIdentity();
    if (stored) {
      return new Identity(hexToBytes(stored.private_key_hex), stored.public_key_hex, stored.client_id);
    }
    const priv = ed25519.utils.randomPrivateKey();
    const pubHex = bytesToHex(ed25519.getPublicKey(priv));
    const rec: StoredIdentity = {
      id: "singleton",
      private_key_hex: bytesToHex(priv),
      public_key_hex: pubHex,
      client_id: null,
    };
    await putIdentity(rec);
    return new Identity(priv, pubHex, null);
  }

  /** Signe (timestamp + hash du corps) → signature hex (64 octets). */
  sign(timestamp: string, body: Uint8Array): string {
    const digest = bytesToHex(sha256(body));
    const message = utf8ToBytes(`${timestamp}.${digest}`);
    return bytesToHex(ed25519.sign(message, this.priv));
  }

  /** Preuve de possession pour l'enrôlement : signe sa propre clé publique. */
  selfSignature(): string {
    return bytesToHex(ed25519.sign(hexToBytes(this.publicKeyHex), this.priv));
  }

  /** Mémorise le client_id attribué par le core après enrôlement. */
  async setClientId(clientId: string): Promise<void> {
    this.clientId = clientId;
    await putIdentity({
      id: "singleton",
      private_key_hex: bytesToHex(this.priv),
      public_key_hex: this.publicKeyHex,
      client_id: clientId,
    });
  }
}
